"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { resolveTemplateKey } from "@/lib/invoice/document";
import { TEMPLATES } from "@/lib/invoice/templates";
import {
  COPY_LABELS,
  FORMATS,
  TAX_MODES,
  addDays,
  computeTotals,
  fmtMoney,
  lastDayOfMonth,
  lineMaths,
  monthLabel,
  num,
  parsePastedLines,
  projectLine,
  compareText,
  sortItems,
  suggestNumber,
  todayISO,
  uid,
} from "@/lib/invoice/utils";
import { Button, Card, Field, Input, Loading, Modal, Select, Textarea, cx, resizeImage, useFeedback } from "./ui";
import { currencyOptions, docTypeOptions, stateCode, taxModeOptions } from "./options";
import { CLIENT_BLANK, ClientForm } from "./ClientForm";
import InvoicePreview from "./InvoicePreview";
import TemplatePicker from "./TemplatePicker";
import { TagList, TagPicker, TagSelect } from "./Tags";
import { suggestTag, invoiceTags } from "@/lib/invoice/tags";
import { invoiceChecks, projectToLine } from "@/lib/invoice/billing";
import { saleAmount, saleToLine, unbilledSales } from "@/lib/invoice/sales";
import { buildCatalog } from "@/lib/invoice/catalog";
import { FEATURES, featureDefaults, presetById, presetSchedule, presetTerms } from "@/lib/invoice/presets";
import ServiceInput from "./ServiceInput";
import IndustryPicker from "./IndustryPicker";

// Pick the right GST treatment from where the client is.
export function autoTaxMode(client, settings, brand) {
  if (client?.taxMode) return client.taxMode;
  const country = (client?.country || "").trim().toLowerCase();
  const homeCountry = (brand?.country || "India").trim().toLowerCase();
  if (homeCountry !== "india") {
    // businesses outside India: charge VAT/sales tax at home, nothing abroad
    if (!country || country === homeCountry) return brand?.gstin ? "vat" : settings.defaults.taxMode === "vat" ? "vat" : "none";
    return "none";
  }
  if (country && country !== "india") return "export";
  if (!country && !client?.state) return settings.defaults.taxMode;
  const home = (brand?.state || "").toLowerCase();
  return client?.state && home && client.state.toLowerCase() !== home ? "inter" : "intra";
}

export function snapshotClient(c) {
  if (!c) return {};
  const { name, contactPerson, email, phone, address, area, city, state, postcode, country, gstin } = c;
  return { name, contactPerson, email, phone, address, area: area || "", city, state, postcode: postcode || "", country, gstin };
}

export function placeOfSupply(c) {
  if (!c?.state) return "";
  const code = stateCode(c.state);
  return code ? `${c.state} (${code})` : c.state;
}

export function clientDefaults(c, store, date) {
  const brand = store.brandById(c.brandId);
  const dueDays = c.dueDays !== "" && c.dueDays != null ? num(c.dueDays) : num(store.settings.defaults.dueDays);
  return {
    clientId: c.id,
    billTo: snapshotClient(c),
    brandId: brand?.id || "",
    currency: c.currency || store.settings.defaults.currency,
    taxMode: autoTaxMode(c, store.settings, brand),
    placeOfSupply: placeOfSupply(c),
    dueDate: dueDays ? addDays(date, dueDays) : "",
  };
}

const newLine = (defaults = {}) => ({ id: uid(), description: "", hsn: "", qty: 1, rate: "", taxRate: 18, ...defaults });

// What an industry starter changes on a document (see presets.js).
export function presetPatch(preset, inv, store) {
  const brand = store.brandById(inv.brandId);
  const india = inv.taxMode === "intra" || inv.taxMode === "inter" || inv.currency === "INR";
  return {
    industry: preset.id,
    subject: preset.subject,
    items: preset.items
      .filter((it) => !it.optional || (inv.docType || "invoice") === "quote")
      .map((it) => newLine({ description: it.description, details: it.details || "", section: it.section || "", optional: !!it.optional, qty: it.qty ?? 1, taxRate: store.settings.defaults.taxRate })),
    notes: preset.notes || "",
    terms: presetTerms(preset, { company: brand?.name }),
    ...(india ? { showHsn: true } : {}),
    ...((inv.docType || "invoice") === "quote" && preset.validityDays ? { dueDate: addDays(inv.date, preset.validityDays) } : {}),
    ...(!inv.template && preset.template ? { template: preset.template } : {}),
    ...optionsPatch(featureDefaults(preset.id), preset, inv),
  };
}

// Document options (see FEATURES in presets.js) and what switching them on fills in.
export function optionsPatch(options, preset, inv) {
  const quoteLike = ["quote", "proforma"].includes(inv.docType || "invoice");
  return {
    options,
    taxInclusive: !!options.inclusive,
    ...(options.schedule && quoteLike && preset?.schedule ? { schedule: presetSchedule(preset) } : {}),
  };
}

// Which options a document shows: what it was set to, plus anything its lines already use.
export function docOptions(inv) {
  const items = inv.items || [];
  const used = {
    sections: items.some((l) => String(l.section || "").trim()),
    optional: items.some((l) => l.optional),
    photos: items.some((l) => l.image),
    discount: items.some((l) => num(l.discount)),
    inclusive: !!inv.taxInclusive,
    schedule: (inv.schedule || []).length > 0,
  };
  return Object.fromEntries(Object.keys(FEATURES).map((k) => [k, !!(inv.options?.[k] || used[k])]));
}

export default function InvoiceEditor() {
  const store = useInvoiceStore();
  const router = useRouter();
  const params = useSearchParams();
  const { toast, confirm } = useFeedback();
  const { settings } = store;
  const [inv, setInv] = useState(null);
  const [numberSeq, setNumberSeq] = useState(null); // null => manual number
  const [tab, setTab] = useState("edit");
  const [picker, setPicker] = useState(null);
  const [paste, setPaste] = useState(null);
  const [newClient, setNewClient] = useState(null);
  const [showDesign, setShowDesign] = useState(false);
  const [fxLoading, setFxLoading] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [openDetails, setOpenDetails] = useState(() => new Set());
  const [dragId, setDragId] = useState(null);
  const [termsLib, setTermsLib] = useState(null); // null = closed, else name for "save current"
  const moved = useRef(false);
  const drag = useRef(null); // { id, move, end } – handlers refreshed every render
  const [dirty, setDirty] = useState(false);

  // ---------- initialise ----------
  useEffect(() => {
    if (!store.ready || inv) return;
    const id = params.get("id");
    const dup = params.get("duplicate");
    const d = settings.defaults;
    if (id) {
      const found = store.invoices.find((x) => x.id === id);
      if (found) {
        setInv(structuredClone(found));
        return;
      }
    }
    let base = {
      docType: params.get("type") || "invoice",
      status: "draft",
      brandId: settings.defaultBrandId,
      template: "",
      date: todayISO(),
      dueDate: d.dueDays ? addDays(todayISO(), d.dueDays) : "",
      currency: d.currency,
      taxMode: d.taxMode,
      clientId: "",
      billTo: {},
      items: [],
      discount: "",
      credit: "",
      creditLabel: "Credit Amount",
      notes: d.notes,
      terms: d.terms,
      copyLabel: COPY_LABELS[0],
      showHsn: !!d.showHsn,
      manualOrder: d.lineOrder === "manual",
      showClientLogo: d.showClientLogo,
      period: "",
      periodMonth: "",
      placeOfSupply: "",
      reference: "",
      fxRate: "",
      payments: [],
      history: [],
    };
    // switch on the document options this business type needs (Settings → business profile → business type)
    const industry = store.brandById(base.brandId)?.industry;
    if (industry) Object.assign(base, optionsPatch(featureDefaults(industry), presetById(industry), base));
    if (dup) {
      const src = store.invoices.find((x) => x.id === dup);
      if (src) {
        const { id: _i, number: _n, createdAt: _c, updatedAt: _u, history: _h, ...rest } = structuredClone(src);
        const asType = params.get("type") || rest.docType;
        base = {
          ...rest,
          docType: asType,
          status: "draft",
          payments: [],
          history: [],
          date: todayISO(),
          items: rest.items.map((l) => ({ ...l, id: uid() })),
        };
        if (src.docType === "quote" && asType === "invoice") base.fromQuoteId = src.id;
        else delete base.fromQuoteId;
        if (src.docType === "quote" && asType !== "quote") {
          // only the optional add-ons the client chose go on to the invoice
          base.items = base.items.filter((l) => !l.optional || l.chosen).map(({ optional: _o, chosen: _c, ...l }) => l);
        }
        const stage = (src.schedule || [])[Number(params.get("stage"))];
        if (stage && asType !== "quote") {
          // invoice for one payment stage: every line at the stage's share
          const pct = num(stage.percent) / 100;
          base.items = base.items.map((l) => ({ ...l, rate: Math.round(num(l.rate) * pct * 100) / 100 }));
          base.discount = num(src.discount) ? Math.round(num(src.discount) * pct * 100) / 100 : "";
          base.credit = "";
          base.roundOff = "";
          base.schedule = [];
          base.stageOf = { id: src.id, index: Number(params.get("stage")) };
          const no = src.schedule.indexOf(stage) + 1;
          base.subject = `Stage ${no} of ${src.schedule.length}: ${stage.label} (${+num(stage.percent).toFixed(2)}%) – ${src.docType === "quote" ? "quotation" : "invoice"} ${src.number}`;
        }
        if (asType === "credit") {
          base.againstNumber = src.number;
          base.againstId = src.id;
          base.credit = "";
          base.discount = "";
          base.dueDate = "";
          base.notes = `Credit note against invoice ${src.number}.`;
        } else if (src.dueDate && src.date) {
          const gap = Math.round((new Date(src.dueDate) - new Date(src.date)) / 864e5);
          base.dueDate = addDays(base.date, gap);
        }
      }
    }
    const client = store.clients.find((c) => c.id === params.get("client"));
    if (client) {
      base = { ...base, ...clientDefaults(client, store, base.date) };
      const ids = (params.get("projects") || "").split(",").filter(Boolean);
      const ym = base.periodMonth || (base.date || "").slice(0, 7);
      base.items = store.projects
        .filter((p) => ids.includes(p.id))
        .map((p) => (p.billing === "monthly" && projectToLine(p, ym, { basis: settings.defaults.prorate || "30" })) || projectToLine({ ...p, billing: "one-time" }, ym))
        .map((l) => newLine(l));
      const saleIds = (params.get("sales") || "").split(",").filter(Boolean);
      const sales = store.sales.filter((x) => saleIds.includes(x.id) && !x.invoiceId && x.clientId === client.id);
      base.items = [...base.items, ...sales.map((x) => saleToLine(x, store.projects, settings.defaults.taxRate))];
      if (!base.manualOrder) base.items = sortItems(base.items);
    }
    const preset = !dup && presetById(params.get("preset"));
    if (preset) {
      base = { ...base, ...presetPatch(preset, base, store) };
      if (!base.manualOrder) base.items = sortItems(base.items);
    }
    const s = suggestNumber(settings, store.invoices, base.docType, base.date, store.brandById(base.brandId));
    base.number = s.number;
    setNumberSeq(s.seq);
    setInv(base);
  }, [store, settings, params, inv]);

  // Keep auto-number in sync with doc type / date / brand while untouched.
  useEffect(() => {
    if (!inv || inv.id || numberSeq == null) return;
    const s = suggestNumber(settings, store.invoices, inv.docType, inv.date, store.brandById(inv.brandId));
    if (s.number !== inv.number) {
      setInv((v) => ({ ...v, number: s.number }));
      setNumberSeq(s.seq);
    }
  }, [inv?.docType, inv?.date, inv?.brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const totals = useMemo(() => (inv ? computeTotals(inv) : null), [inv]);
  const catalog = useMemo(() => (inv ? buildCatalog(store, { clientId: inv.clientId, excludeId: inv.id }) : []), [store, inv?.clientId, inv?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch) => {
    setDirty(true);
    setInv((v) => ({ ...v, ...patch }));
  };
  const setLine = (id, patch) => set({ items: inv.items.map((l) => (l.id === id ? { ...l, ...patch } : l)) });

  // client defaults, keeping a due / valid-until date already set when the client has none
  const defaultsFor = (c) => {
    const defs = clientDefaults(c, store, inv.date);
    if (!defs.dueDate && inv.dueDate) delete defs.dueDate;
    return defs;
  };
  const chooseClient = (id) => {
    const c = store.clients.find((x) => x.id === id);
    if (!c) return set({ clientId: "", billTo: {} });
    const defs = defaultsFor(c);
    set(inv.id ? { clientId: defs.clientId, billTo: defs.billTo, placeOfSupply: defs.placeOfSupply } : defs);
  };

  const applyFormat = (key) => set({ currency: FORMATS[key].currency, taxMode: FORMATS[key].taxMode });

  const duplicateNumber = inv && store.invoices.some((x) => x.number === inv.number && x.id !== inv.id);
  const locked = inv?.id && inv.status !== "draft";

  const save = useCallback(
    async (status) => {
      if (!inv) return;
      if (!inv.clientId && !inv.billTo?.name) return toast("Choose a client first.", { tone: "error" });
      if (!inv.number?.trim()) return toast("Invoice number is required.", { tone: "error" });
      if (duplicateNumber && !(await confirm(`Number ${inv.number} is already used by another document. Save anyway?`, { okLabel: "Save anyway" }))) return;
      const items = inv.items
        .filter((l) => l.description?.trim() || num(l.rate))
        .map((l) => ({ ...l, qty: num(l.qty), rate: num(l.rate), taxRate: num(l.taxRate) }));
      if (!items.length) return toast("Add at least one line item.", { tone: "error" });
      if (!inv.manualOrder) items.splice(0, items.length, ...sortItems(items));
      const nextStatus = status || inv.status;
      const action = !inv.id ? (nextStatus === "draft" ? "Created as draft" : "Created and finalised") : inv.status === "draft" && nextStatus !== "draft" ? "Finalised" : "Edited";
      const record = store.saveInvoice(
        { ...inv, items, status: nextStatus, discount: num(inv.discount), credit: num(inv.credit), fxRate: inv.fxRate === "" ? "" : num(inv.fxRate) },
        { seqUsed: !inv.id && numberSeq != null ? numberSeq : null, action }
      );
      // invoicing a quotation marks it accepted
      const quote = !inv.id && inv.fromQuoteId && store.invoices.find((x) => x.id === inv.fromQuoteId);
      if (quote && ["draft", "sent", undefined].includes(quote.status)) store.saveInvoice({ ...quote, status: "accepted" }, { action: `Accepted – invoiced as ${record.number}` });
      setDirty(false);
      toast("Saved", { tone: "success" });
      router.push(`/invoice/invoices/view?id=${record.id}`);
    },
    [inv, duplicateNumber, numberSeq, store, router, toast, confirm]
  );

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  if (!store.ready || !inv) return <Loading />;

  const taxable = inv.taxMode === "intra" || inv.taxMode === "inter" || inv.taxMode === "vat";
  // the client's projects/services plus reusable services offered to every client
  const clientProjects = store.projects.filter((p) => (inv.clientId && p.clientId === inv.clientId) || (p.kind === "service" && !p.clientId));
  // lines are always kept A–Z (blank lines stay at the bottom until typed in)
  // A–Z unless the user has arranged the lines by hand
  const ordered = (items) => (inv.manualOrder ? items : sortItems(items));

  // ---------- drag to reorder (mouse, touch and ↑/↓ keys) ----------
  const moveLine = (id, to) => {
    const items = [...inv.items];
    const from = items.findIndex((x) => x.id === id);
    if (from < 0 || to < 0 || to >= items.length || from === to) return;
    items.splice(to, 0, items.splice(from, 1)[0]);
    moved.current = true;
    set({ items, manualOrder: true });
  };
  // the first rearrangement tells us this user wants their own order – remember it for new documents
  const finishMove = () => {
    if (!moved.current) return;
    moved.current = false;
    if (settings.defaults.lineOrder !== "manual") {
      store.updateSettings({ defaults: { ...settings.defaults, lineOrder: "manual" } });
      toast("Got it – lines now stay in the order you arrange them, on this and new documents. “Sort A–Z” switches back.", { tone: "success" });
    }
  };
  // Follow the pointer on the window (attached on press): when a row moves React
  // re-inserts it, which would drop pointer capture on the handle itself.
  drag.current = {
    ...drag.current,
    move: (e) => {
      const id = drag.current?.id;
      const row = id && document.elementFromPoint(e.clientX, e.clientY)?.closest("tr[data-line]");
      if (row && row.dataset.line !== id) moveLine(id, inv.items.findIndex((x) => x.id === row.dataset.line));
    },
    end: () => {
      drag.current.id = null;
      setDragId(null);
      finishMove();
    },
  };
  const startDrag = (id) => {
    drag.current.id = id;
    setDragId(id);
    const move = (e) => drag.current.move(e);
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      drag.current.end();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };
  // ---------- document options ----------
  const opts = docOptions(inv);
  const sectionNames = [...new Set(inv.items.map((l) => String(l.section || "").trim()).filter(Boolean))];
  const toggleOption = async (k) => {
    const on = !opts[k];
    const patch = { options: { ...opts, [k]: on } };
    if (k === "inclusive") patch.taxInclusive = on;
    if (k === "schedule") {
      if (on && !(inv.schedule || []).length) {
        const p = presetById(inv.industry) || presetById(store.brandById(inv.brandId)?.industry);
        patch.schedule = p?.schedule ? presetSchedule(p) : [
          { id: uid(), label: "Advance", percent: 50 },
          { id: uid(), label: "On delivery / completion", percent: 50 },
        ];
      }
      if (!on) patch.schedule = [];
    }
    if (!on) {
      const drop = { sections: "section", optional: "optional", photos: "image", discount: "discount" }[k];
      const used = drop && inv.items.some((l) => (drop === "discount" ? num(l[drop]) : l[drop]));
      if (used && !(await confirm(`Turn off ${FEATURES[k].label.toLowerCase()}? It will be removed from the lines on this document.`, { okLabel: "Turn off" }))) return;
      if (used) patch.items = inv.items.map((l) => ({ ...l, [drop]: drop === "optional" ? false : "", ...(drop === "optional" ? { chosen: false } : {}) }));
      if (k === "schedule" && (inv.schedule || []).length && !(await confirm("Remove the payment schedule from this document?", { okLabel: "Remove" }))) return;
    }
    set(patch);
  };
  const addPhoto = async (id, file) => {
    if (!file) return;
    try {
      setLine(id, { image: await resizeImage(file, 220, "image/jpeg") });
    } catch {
      toast("Couldn’t read that image", { tone: "error" });
    }
  };
  const schedule = inv.schedule || [];
  const setStage = (sid, patch) => set({ schedule: schedule.map((x) => (x.id === sid ? { ...x, ...patch } : x)) });
  const schedulePct = Math.round(schedule.reduce((t, x) => t + num(x.percent), 0) * 100) / 100;

  // ---------- saved terms library ----------
  const library = settings.termsLibrary || [];
  const saveTerms = (name) => {
    if (!name.trim() || !inv.terms?.trim()) return;
    const existing = library.find((t) => t.name.toLowerCase() === name.trim().toLowerCase());
    store.updateSettings({ termsLibrary: existing ? library.map((t) => (t.id === existing.id ? { ...t, text: inv.terms } : t)) : [...library, { id: uid(), name: name.trim(), text: inv.terms }] });
    toast(existing ? `Updated “${name.trim()}”` : `Saved “${name.trim()}” to your terms library`, { tone: "success" });
    setTermsLib(null);
  };

  const sortAZ = () => {
    set({ items: sortItems(inv.items), manualOrder: false });
    if (settings.defaults.lineOrder === "manual") {
      store.updateSettings({ defaults: { ...settings.defaults, lineOrder: "az" } });
      toast("Sorted A–Z – new documents will sort A–Z again.");
    }
  };
  const cur = inv.currency;
  const client = store.clients.find((c) => c.id === inv.clientId);
  const effectiveTemplate = resolveTemplateKey(inv, store);
  const usedSaleIds = new Set(inv.items.map((l) => l.saleId).filter(Boolean));
  const pendingSales =
    (inv.docType || "invoice") === "invoice" && inv.clientId ? unbilledSales(store.sales, { clientId: inv.clientId }).filter((x) => !usedSaleIds.has(x.id)) : [];

  const fetchFx = async () => {
    setFxLoading(true);
    try {
      const r = await fetch(`https://api.frankfurter.app/${inv.date}?from=${cur}&to=INR`);
      const j = await r.json();
      const rate = j?.rates?.INR;
      if (!rate) throw new Error("no rate");
      set({ fxRate: rate });
      toast(`1 ${cur} = ₹${rate} on ${j.date} (ECB reference rate)`);
    } catch {
      toast("Couldn’t fetch the rate – enter it manually (e.g. RBI/SBI TT rate).", { tone: "error" });
    } finally {
      setFxLoading(false);
    }
  };

  const openPicker = () => {
    const used = new Set(inv.items.map((l) => l.projectId).filter(Boolean));
    setPicker(new Set(clientProjects.filter((p) => p.clientId && p.status === "active" && !used.has(p.id)).map((p) => p.id)));
  };
  const addPicked = () => {
    const ym = inv.periodMonth || (inv.date || "").slice(0, 7);
    const lines = clientProjects
      .filter((p) => picker.has(p.id))
      .map((p) => (p.billing === "monthly" && projectToLine(p, ym, { basis: settings.defaults.prorate || "30" })) || projectToLine({ ...p, billing: "one-time" }, ym))
      .map((l) => newLine({ ...l, taxRate: l.taxRate || settings.defaults.taxRate }));
    set({ items: ordered([...inv.items.filter((l) => l.description || num(l.rate)), ...lines]) });
    setPicker(null);
  };

  const pickService = (id, line) => {
    const { projectId, ...rest } = line;
    set({
      items: inv.items.map((l) =>
        l.id === id
          ? { ...l, ...rest, rate: rest.rate || l.rate, taxRate: taxable && rest.taxRate != null && rest.taxRate !== "" ? rest.taxRate : l.taxRate, ...(projectId && clientProjects.some((p) => p.id === projectId && p.clientId) ? { projectId } : {}) }
          : l
      ),
      ...(line.hsn && !inv.showHsn ? { showHsn: true } : {}),
    });
    if (line.details) setOpenDetails((s) => new Set(s).add(id));
  };

  const applyPreset = async (preset) => {
    const filled = inv.items.some((l) => l.description?.trim() || num(l.rate));
    if (filled && !(await confirm(`Replace the current lines, subject, notes and terms with the “${preset.label}” starter?`, { okLabel: "Replace" }))) return;
    const patch = presetPatch(preset, inv, store);
    patch.items = ordered(patch.items);
    set(patch);
    setOpenDetails(new Set(patch.items.filter((l) => l.details).map((l) => l.id)));
    setShowPresets(false);
    toast(`${preset.label} starter applied – add your prices`, { tone: "success" });
  };

  const saveAsService = (l) => {
    const name = l.description.trim();
    if (!name) return;
    const existing = store.projects.find((p) => p.kind === "service" && !p.clientId && projectLine(p).toLowerCase() === name.toLowerCase());
    store.saveProject({
      ...(existing || { kind: "service", clientId: "", service: "", billing: "one-time", status: "active", qty: 1 }),
      name: existing ? existing.name : name,
      service: existing ? existing.service : "",
      rate: num(l.rate),
      taxRate: num(l.taxRate),
      hsn: l.hsn || "",
      details: l.details || "",
      tag: l.tag || existing?.tag || "",
    });
    toast(existing ? `Updated saved service “${name}”` : `Saved “${name}” – it will be suggested as you type`, { tone: "success" });
  };

  const saveButtons = (
    <>
      <Button variant="secondary" onClick={() => save("draft")}>Save draft</Button>
      <Button onClick={() => save(inv.status === "draft" ? (inv.docType === "credit" ? "issued" : "sent") : inv.status)}>Save & finalise</Button>
    </>
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[26px] font-semibold tracking-tight">{inv.id ? `Edit ${inv.number}` : `New ${docTypeOptions.find((o) => o.value === inv.docType)?.label.toLowerCase()}`}</div>
          <div className="text-sm text-slate-500">Ctrl/⌘ + S to save{dirty ? " · unsaved changes" : ""}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5 text-sm">
            {["edit", "preview"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={cx("rounded-md px-3 py-1.5 capitalize", tab === t ? "bg-slate-900 text-white" : "text-slate-600")}>
                {t}
              </button>
            ))}
          </div>
          <Button variant="secondary" onClick={() => setShowPresets(true)} data-testid="industry-starters">Industry templates</Button>
          <Button variant="secondary" onClick={() => router.back()}>Cancel</Button>
          {saveButtons}
        </div>
      </div>

      {locked && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          This document has already been finalised{inv.status === "sent" ? " and probably sent to the client" : ""}. For GST, prefer issuing a{" "}
          <b>credit note</b> instead of changing amounts on a sent invoice. Edits are recorded in the invoice history.
        </div>
      )}

      {tab === "preview" ? (
        <div className="overflow-x-auto rounded-2xl bg-slate-200/60 p-4 ring-1 ring-inset ring-slate-900/5 sm:p-6">
          <InvoicePreview invoice={inv} data={store} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_320px]">
          <div className="min-w-0 space-y-5">
            <Card
              title="Details"
              actions={Object.entries(FORMATS).map(([k, f]) => (
                <Button key={k} size="sm" variant={f.currency === cur && f.taxMode === inv.taxMode ? "primary" : "secondary"} onClick={() => applyFormat(k)}>
                  {f.label}
                </Button>
              ))}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Document type">
                  <Select value={inv.docType} onChange={(e) => set({ docType: e.target.value })} options={docTypeOptions} disabled={!!inv.id} />
                </Field>
                <Field label="Number" hint={duplicateNumber ? <span className="text-brand-600">Already used!</span> : numberSeq != null ? "Auto" : "Manual"}>
                  <div className="flex gap-1">
                    <Input
                      value={inv.number}
                      onChange={(e) => {
                        set({ number: e.target.value });
                        setNumberSeq(null);
                      }}
                    />
                    {!inv.id && (
                      <button
                        title="Regenerate next number"
                        className="rounded-lg border border-slate-300 px-2 text-slate-500 hover:bg-slate-50"
                        onClick={() => {
                          const s = suggestNumber(settings, store.invoices, inv.docType, inv.date, store.brandById(inv.brandId));
                          set({ number: s.number });
                          setNumberSeq(s.seq);
                        }}
                      >
                        ↻
                      </button>
                    )}
                  </div>
                </Field>
                <Field label="Date">
                  <Input type="date" value={inv.date} onChange={(e) => set({ date: e.target.value })} />
                </Field>
                {inv.docType !== "credit" ? (
                  <Field label={inv.docType === "quote" ? "Valid until" : "Due date"}>
                    <Input type="date" value={inv.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {[["N/A", null], ["7d", 7], ["15d", 15], ["30d", 30], ["45d", 45]].map(([l, dd]) => (
                        <button key={l} className="rounded bg-slate-100 px-1.5 text-[11px] text-slate-600 hover:bg-slate-200" onClick={() => set({ dueDate: dd == null ? "" : addDays(inv.date, dd) })}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </Field>
                ) : (
                  <Field label="Against invoice no.">
                    <Input value={inv.againstNumber || ""} onChange={(e) => set({ againstNumber: e.target.value })} />
                  </Field>
                )}
                <Field label="Currency">
                  <Select value={cur} onChange={(e) => set({ currency: e.target.value })} options={currencyOptions} />
                </Field>
                <Field label="Tax treatment" className={inv.taxMode === "vat" ? "" : "lg:col-span-2"}>
                  <Select value={inv.taxMode} onChange={(e) => set({ taxMode: e.target.value })} options={taxModeOptions} />
                </Field>
                {inv.taxMode === "vat" && (
                  <Field label="Tax name on invoice" hint="e.g. VAT, Sales tax, GST, HST">
                    <Input value={inv.taxLabel ?? ""} placeholder="VAT" onChange={(e) => set({ taxLabel: e.target.value })} />
                  </Field>
                )}
                {cur !== "INR" ? (
                  <Field label={`Rate: 1 ${cur} in INR`} hint="For GST returns (export value in ₹)">
                    <div className="flex gap-1">
                      <Input type="number" step="any" value={inv.fxRate ?? ""} onChange={(e) => set({ fxRate: e.target.value })} placeholder="e.g. 112.45" />
                      <button className="whitespace-nowrap rounded-lg border border-slate-300 px-2 text-xs text-slate-600 hover:bg-slate-50" onClick={fetchFx} disabled={fxLoading}>
                        {fxLoading ? "…" : "Fetch"}
                      </button>
                    </div>
                  </Field>
                ) : (
                  <div className="hidden lg:block" />
                )}
                <Field label="Billing period">
                  <Input type="month" value={inv.periodMonth || ""} onChange={(e) => set({ periodMonth: e.target.value, period: e.target.value ? monthLabel(e.target.value) : "" })} />
                  {inv.periodMonth && (
                    <button className="mt-1 text-[11px] text-brand-700 hover:underline" onClick={() => set({ date: lastDayOfMonth(inv.periodMonth) })}>
                      Set date to month end
                    </button>
                  )}
                </Field>
                <Field label="Place of supply">
                  <Input value={inv.placeOfSupply} onChange={(e) => set({ placeOfSupply: e.target.value })} placeholder="Rajasthan (08)" />
                </Field>
                <Field label="Subject" className="sm:col-span-2 lg:col-span-4" hint="Optional one-line heading shown above the items, e.g. “Quotation for 3 kW rooftop solar system”">
                  <Input value={inv.subject || ""} onChange={(e) => set({ subject: e.target.value })} placeholder={inv.docType === "quote" ? "Quotation for …" : ""} />
                </Field>
                <Field label="Reference / PO" className="lg:col-span-2">
                  <Input value={inv.reference} onChange={(e) => set({ reference: e.target.value })} />
                </Field>
                <Field
                  label="Extra tags"
                  className="sm:col-span-2 lg:col-span-4"
                  hint={
                    <span className="flex flex-wrap items-center gap-1">
                      Categories from the lines are included automatically
                      {invoiceTags({ items: inv.items }).length > 0 && (
                        <>
                          : <TagList ids={invoiceTags({ items: inv.items })} tags={settings.tags} small max={8} />
                        </>
                      )}
                    </span>
                  }
                >
                  <TagPicker tags={settings.tags} value={inv.tags || []} onChange={(tags) => set({ tags })} />
                </Field>
              </div>
            </Card>

            <Card
              title="Bill to"
              actions={<Button size="sm" variant="secondary" onClick={() => setNewClient({ ...CLIENT_BLANK })}>+ New client</Button>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Select value={inv.clientId} onChange={(e) => chooseClient(e.target.value)}>
                  <option value="">Choose client…</option>
                  {[...store.clients]
                    .filter((c) => !c.archived || c.id === inv.clientId)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </Select>
                {inv.billTo?.name && (
                  <div className="flex gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {client?.logo && <img src={client.logo} alt="" className="h-10 w-10 object-contain" />}
                    <div>
                      <div className="font-semibold">{inv.billTo.name}</div>
                      <div className="text-xs">
                        {[inv.billTo.address, inv.billTo.city, inv.billTo.country].filter(Boolean).join(", ")}
                        {inv.billTo.gstin && ` · GSTIN ${inv.billTo.gstin}`}
                      </div>
                      {inv.clientId && (
                        <button className="mt-1 text-[11px] text-brand-700 hover:underline" onClick={() => chooseClient(inv.clientId)}>
                          Refresh from client record
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card
              title={`Line items (${inv.items.length})`}
              subtitle={inv.manualOrder ? "In your order – drag ⠿ to rearrange" : "A–Z by description – drag ⠿ to arrange your own order"}
              actions={
                <>
                  {inv.manualOrder && inv.items.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={sortAZ} data-testid="sort-az">
                      Sort A–Z
                    </Button>
                  )}
                  <label className="flex items-center gap-1.5 text-xs text-slate-600">
                    <input type="checkbox" checked={!!inv.showHsn} onChange={(e) => set({ showHsn: e.target.checked })} /> HSN/SAC
                  </label>
                  <Button size="sm" variant="secondary" disabled={!inv.clientId || clientProjects.length === 0} onClick={openPicker}>
                    Add projects / services
                  </Button>
                  {pendingSales.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => {
                        set({ items: ordered([...inv.items.filter((l) => l.description || num(l.rate)), ...pendingSales.map((x) => saleToLine(x, store.projects, settings.defaults.taxRate))]) });
                        toast(`Added ${pendingSales.length} unbilled sale(s)`, { tone: "success" });
                      }}
                    >
                      + {pendingSales.length} unbilled sale{pendingSales.length > 1 ? "s" : ""} ({fmtMoney(pendingSales.reduce((t, x) => t + saleAmount(x), 0), cur)})
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setPaste("")}>Paste lines</Button>
                  {inv.items.length > 0 && (
                    <Button size="sm" variant="ghost" onClick={async () => (await confirm("Remove all line items?", { okLabel: "Remove all", danger: true })) && set({ items: [] })}>
                      Clear
                    </Button>
                  )}
                </>
              }
            >
              <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="doc-options">
                <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Options</span>
                {Object.entries(FEATURES)
                  .filter(([k]) => (k !== "optional" || inv.docType === "quote") && (k !== "inclusive" || taxable) && (k !== "schedule" || inv.docType !== "credit"))
                  .map(([k, f]) => (
                    <button
                      key={k}
                      type="button"
                      title={f.hint}
                      data-testid={`opt-${k}`}
                      onClick={() => toggleOption(k)}
                      className={cx("rounded-full border px-2.5 py-0.5 text-xs transition", opts[k] ? "border-brand-600 bg-brand-50 font-medium text-brand-800" : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700")}
                    >
                      {opts[k] ? "✓ " : "+ "}
                      {f.label}
                    </button>
                  ))}
              </div>
              <datalist id="section-names">
                {sectionNames.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="w-12 pb-2">#</th>
                      <th className="pb-2">Description</th>
                      {inv.showHsn && <th className="w-24 pb-2">HSN/SAC</th>}
                      <th className="w-16 pb-2">Qty</th>
                      <th className="w-28 pb-2">{inv.taxInclusive && taxable ? "Rate incl. tax" : "Rate"}</th>
                      {opts.discount && <th className="w-16 pb-2">Disc %</th>}
                      {taxable && <th className="w-16 pb-2">GST %</th>}
                      <th className="w-32 pb-2">Category</th>
                      <th className="w-28 pb-2 text-right">Amount</th>
                      <th className="w-24 pb-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {inv.items.map((l, i) => (
                      <tr key={l.id} data-line={l.id} className={cx("align-top transition-colors", dragId === l.id && "bg-brand-50/70")}>
                        <td className="py-1 pr-1 pt-2">
                          <button
                            type="button"
                            aria-label={`Move line ${i + 1}`}
                            title="Drag to reorder (or focus and press ↑ / ↓)"
                            data-testid="drag-handle"
                            className={cx(
                              "flex h-8 cursor-grab touch-none select-none items-center gap-1 rounded px-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing",
                              dragId === l.id && "text-brand-600"
                            )}
                            onPointerDown={(e) => {
                              e.preventDefault();
                              startDrag(l.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                              e.preventDefault();
                              moveLine(l.id, i + (e.key === "ArrowUp" ? -1 : 1));
                              finishMove();
                            }}
                          >
                            <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor" aria-hidden="true">
                              {[1, 5, 9, 13].flatMap((y) => [1.5, 6.5].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y - 0.5} r="1.2" />))}
                            </svg>
                            <span className="text-xs text-slate-400">{i + 1}</span>
                          </button>
                        </td>
                        <td className="py-1 pr-1">
                          <ServiceInput
                            className="px-2 py-1.5"
                            value={l.description}
                            catalog={catalog}
                            currency={cur}
                            onChange={(description) => setLine(l.id, { description })}
                            onPick={(line) => pickService(l.id, line)}
                            onBlur={() =>
                              setInv((v) => ({
                                ...v,
                                items: ((items) => (v.manualOrder ? items : sortItems(items)))(
                                  v.items.map((x) => (x.id === l.id && !x.tag && x.description ? { ...x, tag: suggestTag(x.description, settings.tags) } : x))
                                ),
                              }))
                            }
                            placeholder={catalog.length ? "Type a product / service – saved ones are suggested" : "Product / service description"}
                          />
                          {(openDetails.has(l.id) || l.details) && (
                            <Textarea
                              className="mt-1 !min-h-[52px] px-2 py-1.5 text-xs"
                              value={l.details || ""}
                              placeholder="Details shown under the item – specifications, scope, inclusions (one per line)"
                              onChange={(e) => setLine(l.id, { details: e.target.value })}
                            />
                          )}
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                            {!(openDetails.has(l.id) || l.details) && (
                              <button className="hover:text-brand-700" onClick={() => setOpenDetails((s) => new Set(s).add(l.id))}>
                                + details
                              </button>
                            )}
                            {opts.sections && (
                              <input
                                list="section-names"
                                className="w-36 rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
                                placeholder="Section, e.g. Supply"
                                value={l.section || ""}
                                onChange={(e) => setLine(l.id, { section: e.target.value })}
                                data-testid="line-section"
                              />
                            )}
                            {opts.optional && inv.docType === "quote" && (
                              <label className="flex items-center gap-1 text-slate-600">
                                <input type="checkbox" checked={!!l.optional} onChange={(e) => setLine(l.id, { optional: e.target.checked, chosen: false })} data-testid="line-optional" /> Optional
                              </label>
                            )}
                            {opts.optional && inv.docType === "quote" && l.optional && (
                              <label className="flex items-center gap-1 text-slate-600" title="Tick when the client has chosen this add-on – it then counts in the total">
                                <input type="checkbox" checked={!!l.chosen} onChange={(e) => setLine(l.id, { chosen: e.target.checked })} /> Client chose it
                              </label>
                            )}
                            {opts.photos &&
                              (l.image ? (
                                <span className="flex items-center gap-1">
                                  <img src={l.image} alt="" className="h-7 w-7 rounded object-cover ring-1 ring-slate-200" />
                                  <button className="hover:text-brand-600" title="Remove photo" onClick={() => setLine(l.id, { image: "" })}>
                                    ✕
                                  </button>
                                </span>
                              ) : (
                                <label className="cursor-pointer hover:text-brand-700">
                                  + photo
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => addPhoto(l.id, e.target.files?.[0])} />
                                </label>
                              ))}
                          </div>
                        </td>
                        {inv.showHsn && (
                          <td className="py-1 pr-1">
                            <Input className="px-2 py-1.5" value={l.hsn} onChange={(e) => setLine(l.id, { hsn: e.target.value })} placeholder={settings.defaults.sac} />
                          </td>
                        )}
                        <td className="py-1 pr-1">
                          <Input className="px-2 py-1.5" type="number" step="any" value={l.qty} onChange={(e) => setLine(l.id, { qty: e.target.value })} />
                        </td>
                        <td className="py-1 pr-1">
                          <Input className="px-2 py-1.5" type="number" step="0.01" value={l.rate} onChange={(e) => setLine(l.id, { rate: e.target.value })} />
                        </td>
                        {opts.discount && (
                          <td className="py-1 pr-1">
                            <Input className="px-2 py-1.5" type="number" step="any" min="0" max="100" value={l.discount ?? ""} onChange={(e) => setLine(l.id, { discount: e.target.value })} placeholder="0" />
                          </td>
                        )}
                        {taxable && (
                          <td className="py-1 pr-1">
                            <Input className="px-2 py-1.5" type="number" step="any" value={l.taxRate} onChange={(e) => setLine(l.id, { taxRate: e.target.value })} />
                          </td>
                        )}
                        <td className="py-1 pr-1">
                          <TagSelect className="w-full" tags={settings.tags} value={l.tag} onChange={(tag) => setLine(l.id, { tag })} placeholder="—" />
                        </td>
                        <td className={cx("py-1 pr-1 pt-3 text-right font-medium", l.optional && inv.docType === "quote" && !l.chosen && "text-slate-400")}>
                          {fmtMoney(lineMaths(l, { taxable: false }).amount, cur)}
                          {l.optional && inv.docType === "quote" && <div className="text-[10px] font-normal">{l.chosen ? "chosen" : "not in total"}</div>}
                        </td>
                        <td className="whitespace-nowrap py-1 pt-2 text-right text-slate-400">
                          <button className="px-1 hover:text-amber-600" title="Save as a reusable service (suggested when typing)" disabled={!l.description?.trim()} onClick={() => saveAsService(l)}>☆</button>
                          <button className="px-1 hover:text-slate-700" title="Duplicate line" onClick={() => set({ items: [...inv.items.slice(0, i + 1), { ...l, id: uid() }, ...inv.items.slice(i + 1)] })}>⧉</button>
                          <button className="px-1 hover:text-brand-600" title="Remove" onClick={() => set({ items: inv.items.filter((x) => x.id !== l.id) })}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => set({ items: [...inv.items, newLine({ taxRate: settings.defaults.taxRate })] })}>
                  + Add line
                </Button>
              </div>
            </Card>

            {opts.schedule && (
              <Card
                title="Payment schedule"
                subtitle="Stages as a % of the total. Save the document, then raise an invoice for each stage from its page."
                actions={
                  <Button size="sm" variant="secondary" onClick={() => set({ schedule: [...schedule, { id: uid(), label: "", percent: Math.max(0, 100 - schedulePct) }] })}>
                    + Add stage
                  </Button>
                }
              >
                <div className="space-y-2" data-testid="schedule">
                  {schedule.map((x, i) => (
                    <div key={x.id} className="flex items-center gap-2">
                      <span className="w-5 text-xs text-slate-400">{i + 1}</span>
                      <Input className="flex-1" value={x.label} placeholder="e.g. Advance with order" onChange={(e) => setStage(x.id, { label: e.target.value })} />
                      <div className="flex w-24 items-center gap-1">
                        <Input type="number" step="any" value={x.percent} onChange={(e) => setStage(x.id, { percent: e.target.value })} />
                        <span className="text-xs text-slate-500">%</span>
                      </div>
                      <span className="w-32 text-right text-sm font-medium tabular-nums">{fmtMoney((totals.total * num(x.percent)) / 100, cur)}</span>
                      <button className="px-1 text-slate-400 hover:text-brand-600" onClick={() => set({ schedule: schedule.filter((y) => y.id !== x.id) })}>
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className={cx("text-right text-xs", schedulePct === 100 ? "text-emerald-700" : "text-amber-700")}>
                    Total {schedulePct}%{schedulePct !== 100 && " – stages should add up to 100%"}
                  </div>
                </div>
              </Card>
            )}

            <Card
              title={`Design · ${store.brandById(inv.brandId)?.name || ""} · ${TEMPLATES[effectiveTemplate]?.label}`}
              actions={<Button size="sm" variant="secondary" onClick={() => setShowDesign((s) => !s)}>{showDesign ? "Hide" : "Change"}</Button>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Invoice as (business profile)">
                  <Select value={inv.brandId} onChange={(e) => set({ brandId: e.target.value })}>
                    {store.brands.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Copy label">
                  <Select value={inv.copyLabel} onChange={(e) => set({ copyLabel: e.target.value })}>
                    <option value="">(none)</option>
                    {COPY_LABELS.map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </Field>
                <label className="mt-5 flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!inv.showClientLogo} onChange={(e) => set({ showClientLogo: e.target.checked })} disabled={!client?.logo} />
                  Show client logo {client?.logo ? "" : <span className="text-xs text-slate-400">(no logo on client)</span>}
                </label>
                {(() => {
                  const b = store.brandById(inv.brandId);
                  const has = !!b?.stamp && b.stampOnDocs !== false;
                  return (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={has && inv.showStamp !== false} onChange={(e) => set({ showStamp: e.target.checked })} disabled={!has} data-testid="doc-show-stamp" />
                      Company stamp{" "}
                      {!has && <span className="text-xs text-slate-400">({b?.stamp ? "off for this profile" : "add it in Settings → Business profiles"})</span>}
                    </label>
                  );
                })()}
              </div>
              {showDesign && (
                <div className="mt-4">
                  <TemplatePicker
                    value={inv.template}
                    onChange={(template) => set({ template })}
                    invoice={inv}
                    data={store}
                    allowDefault
                    defaultLabel={`Default (${TEMPLATES[resolveTemplateKey({ ...inv, template: "" }, store)]?.label})`}
                  />
                </div>
              )}
            </Card>

            <Card
              title="Notes & terms"
              actions={
                <Button size="sm" variant="secondary" onClick={() => setTermsLib("")} data-testid="terms-library">
                  Saved terms{library.length ? ` (${library.length})` : ""}
                </Button>
              }
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Notes (shown on invoice)">
                  <Textarea value={inv.notes} onChange={(e) => set({ notes: e.target.value })} />
                </Field>
                <Field label="Terms & conditions">
                  <Textarea value={inv.terms} onChange={(e) => set({ terms: e.target.value })} />
                </Field>
              </div>
            </Card>
          </div>

          <div className="space-y-5">
            <ChecksCard inv={inv} store={store} onFix={(lineId, patch) => setLine(lineId, patch)} />
            <Card title="Summary" className="xl:sticky xl:top-6">
              <div className="space-y-2 text-sm">
                <Row label="Subtotal" value={fmtMoney(totals.subtotal, cur)} />
                {inv.taxMode === "intra" && (
                  <>
                    <Row label="CGST" value={fmtMoney(totals.cgst, cur)} />
                    <Row label="SGST" value={fmtMoney(totals.sgst, cur)} />
                  </>
                )}
                {inv.taxMode === "inter" && <Row label="IGST" value={fmtMoney(totals.igst, cur)} />}
                {inv.taxMode === "vat" && <Row label={inv.taxLabel || "VAT"} value={fmtMoney(totals.vat, cur)} />}
                {inv.taxMode === "export" && <Row label="IGST (export, 0%)" value={fmtMoney(0, cur)} />}
                {totals.lineDiscounts > 0 && <Row label="Line discounts given" value={<span className="text-slate-500">{fmtMoney(totals.lineDiscounts, cur)}</span>} />}
                {totals.optionalLines.length > 0 && <Row label={`Optional items (${totals.optionalLines.length})`} value={<span className="text-slate-500">{fmtMoney(totals.optionalTotal, cur)}</span>} />}
                {inv.docType !== "credit" && (
                  <>
                    <Field label="Discount">
                      <Input type="number" step="0.01" value={inv.discount} onChange={(e) => set({ discount: e.target.value })} />
                    </Field>
                    <Field label="Credit / adjustment">
                      <div className="flex gap-1">
                        <Input className="w-1/2" value={inv.creditLabel} onChange={(e) => set({ creditLabel: e.target.value })} />
                        <Input className="w-1/2" type="number" step="0.01" value={inv.credit} onChange={(e) => set({ credit: e.target.value })} />
                      </div>
                    </Field>
                    <Field label="Round off" hint="Small +/- adjustment so the total is a round figure">
                      <Input type="number" step="0.01" value={inv.roundOff ?? ""} onChange={(e) => set({ roundOff: e.target.value })} />
                    </Field>
                  </>
                )}
                <div className="border-t border-slate-200 pt-2">
                  <Row label={<b>Total</b>} value={<b className="text-lg">{fmtMoney(totals.total, cur)}</b>} />
                  {cur !== "INR" && num(inv.fxRate) > 0 && <Row label="≈ INR" value={fmtMoney(totals.total * num(inv.fxRate), "INR")} />}
                  {totals.paid > 0 && <Row label="Balance due" value={fmtMoney(totals.balance, cur)} />}
                </div>
                <div className="text-xs text-slate-500">{TAX_MODES[inv.taxMode]?.label}</div>
              </div>
              <div className="mt-4 grid gap-2">{saveButtons}</div>
            </Card>
          </div>
        </div>
      )}

      <Modal
        open={!!picker}
        onClose={() => setPicker(null)}
        title="Add projects & services"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setPicker(null)}>Cancel</Button>
            <Button onClick={addPicked}>Add {picker?.size || 0} lines</Button>
          </>
        }
      >
        {picker && (
          <div>
            <div className="mb-2 flex gap-3 text-xs">
              <button className="text-brand-700 hover:underline" onClick={() => setPicker(new Set(clientProjects.map((p) => p.id)))}>All</button>
              <button className="text-brand-700 hover:underline" onClick={() => setPicker(new Set(clientProjects.filter((p) => p.status === "active").map((p) => p.id)))}>Active only</button>
              <button className="text-brand-700 hover:underline" onClick={() => setPicker(new Set())}>None</button>
            </div>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {[...clientProjects]
                .sort((a, b) => compareText(projectLine(a), projectLine(b)))
                .map((p) => (
                  <label key={p.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={picker.has(p.id)}
                      onChange={() =>
                        setPicker((s) => {
                          const n = new Set(s);
                          n.has(p.id) ? n.delete(p.id) : n.add(p.id);
                          return n;
                        })
                      }
                    />
                    <span className="flex-1">
                      {projectLine(p)}
                      {p.kind === "service" && <span className="ml-2 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">{p.clientId ? "Service" : "Service · any client"}</span>}
                    </span>
                    <span className={cx("text-xs capitalize", p.status === "active" ? "text-emerald-600" : "text-slate-400")}>{p.status}</span>
                    <span className="w-24 text-right font-medium">{fmtMoney(num(p.rate), cur)}</span>
                  </label>
                ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={paste !== null}
        onClose={() => setPaste(null)}
        title="Paste lines from Excel / text"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaste(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const rows = parsePastedLines(paste || "").map((r) => newLine({ ...r, taxRate: settings.defaults.taxRate, tag: suggestTag(r.description, settings.tags) }));
                set({ items: ordered([...inv.items.filter((l) => l.description || num(l.rate)), ...rows]) });
                setPaste(null);
              }}
            >
              Add {parsePastedLines(paste || "").length} lines
            </Button>
          </>
        }
      >
        <Field hint="One line per item: “Description ⇥ Rate”, “Description ⇥ Qty ⇥ Rate”, or “Description £66”.">
          <Textarea className="min-h-[240px] font-mono text-xs" value={paste || ""} onChange={(e) => setPaste(e.target.value)} />
        </Field>
      </Modal>

      <Modal
        open={termsLib !== null}
        onClose={() => setTermsLib(null)}
        title="Saved terms & conditions"
        wide
        footer={<Button variant="secondary" onClick={() => setTermsLib(null)}>Close</Button>}
      >
        <p className="mb-3 text-sm text-slate-600">Keep your own terms – e.g. “Retail sale”, “AMC contract”, “Export order” – and drop them into any document.</p>
        {library.length === 0 ? (
          <div className="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">Nothing saved yet – write terms on this document, then save them below.</div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {library.map((t) => (
              <div key={t.id} className="flex items-start gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                  <div className="line-clamp-2 whitespace-pre-line text-xs text-slate-500">{t.text}</div>
                </div>
                <Button size="sm" onClick={() => (set({ terms: t.text }), setTermsLib(null))}>Use</Button>
                <Button size="sm" variant="secondary" onClick={() => (set({ terms: [inv.terms, t.text].filter((x) => x?.trim()).join("\n") }), setTermsLib(null))}>Append</Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => (await confirm(`Delete saved terms “${t.name}”?`, { okLabel: "Delete", danger: true })) && store.updateSettings({ termsLibrary: library.filter((x) => x.id !== t.id) })}
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
          <Field label="Save this document’s terms as" className="flex-1">
            <Input value={termsLib || ""} onChange={(e) => setTermsLib(e.target.value)} placeholder="e.g. Standard retail terms" data-testid="terms-name" />
          </Field>
          <Button disabled={!termsLib?.trim() || !inv.terms?.trim()} onClick={() => saveTerms(termsLib)}>
            Save terms
          </Button>
        </div>
      </Modal>

      <Modal open={showPresets} onClose={() => setShowPresets(false)} title="Start from an industry template" xwide>
        <p className="mb-4 text-sm text-slate-600">
          Fills the subject, typical scope lines with details, notes and numbered terms &amp; conditions for your business type. Your business name is used in the terms – add your own prices, then edit anything.
        </p>
        <IndustryPicker value={inv.industry} onPick={applyPreset} />
      </Modal>

      <Modal
        open={!!newClient}
        onClose={() => setNewClient(null)}
        title="New client"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setNewClient(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!newClient.name.trim()) return toast("Client name is required", { tone: "error" });
                const c = store.saveClient(newClient);
                setNewClient(null);
                set(defaultsFor(c));
              }}
            >
              Save & use
            </Button>
          </>
        }
      >
        {newClient && <ClientForm value={newClient} onChange={setNewClient} brands={store.brands} />}
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-600">{label}</span>
      <span>{value}</span>
    </div>
  );
}


// "Does this look right?" – compares project lines with their price and the last invoice.
export function ChecksCard({ inv, store, onFix }) {
  const checks = invoiceChecks(inv, store, { basis: store.settings.defaults.prorate || "30" });
  if (!checks.length) return null;
  return (
    <Card title={`Checks (${checks.length})`} subtitle="Compared with project prices and the last invoice" className="border-amber-200" data-testid="invoice-checks">
      <ul className="space-y-2 text-sm" data-testid="invoice-checks">
        {checks.map((c, i) => (
          <li key={i} className={cx("rounded-lg px-3 py-2", c.level === "warn" ? "bg-amber-50 text-amber-900" : "bg-sky-50 text-sky-900")}>
            {c.level === "warn" ? "⚠ " : "ℹ "}
            {c.text}
            {c.fix && onFix && (
              <button className="ml-2 text-xs font-semibold underline" onClick={() => onFix(c.lineId, c.fix)}>
                Use usual price
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
